import { useCallback, useEffect, useState } from 'react'
import { Card, Col, Form, Row } from 'react-bootstrap'
import MenuItem from '@mui/material/MenuItem'
import { useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */
import useStyles from 'features/setting/defaults/appointment-durations/style'

/** API */
import AppointmentDurationApi from 'api/setting/defaults/appointment-duration.api'

/** UTILS || SLICE */
import { convertDurationToMinuteOrText } from 'utils/date.utils'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'
import { submitModal, unSubmitModal, showModal, resetModal } from 'app/slice/modal.slice'

/** COMPONENT */
import { swalActive } from 'component/Alert/Swal'
import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextField from 'component/Input/InputTextField'
import InputTextSearch from 'component/Input/InputTextSearch'
import ModalCustom from 'component/ModalCustom/ModalCustom'
import TableCustom from 'component/Table/TableCustom'
import BasicSelect from 'component/Select/BasicSelect'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import TableRowCommon from 'component/Table/TableRowCommon'
import FilterSelect from 'component/Select/FilterSelect'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'
import TextInput from 'new-components/inputs/text-input'
import SelectInput from 'new-components/inputs/select-input'
import PageModal from 'new-components/modal/page-modal'
import SwitchButton from 'new-components/buttons/switch-button'
import { Box } from '@mui/material'

const initStateErrorMessage = {
  APPOINTMENT_DURATION: ``,
  IS_DUPLICATE_APPOINTMENT_DURATION: ``,
  APPOINTMENT_DURATION_STRING_BASE: ``,
  APPOINTMENT_DURATION_STRING_EMPTY: ``,
  APPOINTMENT_DURATION_MAXIMUM_LENGTH: ``,
  APPOINTMENT_DURATION_ANY_REQUIRED: ``
}
const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}
export default function AppointmentDurations() {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [appointmentDurations, setAppointmentDurations] = useState([])

  const [appointmentDurationId, setAppointmentDurationId] = useState(Number)
  const [appointmentDuration, setAppointmentDuration] = useState('5')
  const [appointmentDurationType, setAppointmentDurationType] = useState('MINUTES')
  const [appointmentDurationStatus, setAppointmentDurationStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [isPopup, setIsPopup] = useState(false)
  const [useWith, setUseWith] = useState<"BRANCH" | "USER" | ''>('')

  const loadData = useCallback(async () => {

    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }

    const res = await AppointmentDurationApi.findAll(condition)
    if (res.status === 200) {
      setRowCount(res.headers['x-total'])
      setAppointmentDurations(res.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const submit = () => {
    if (!appointmentDuration) return setErrorMessage({ ...errorMessage, ...{ APPOINTMENT_DURATION: t('APPOINTMENT_DURATION.MESSAGE.APPOINTMENT_DURATION_STRING_EMPTY') } })
    if (appointmentDurationType === 'MINUTES' && Number(appointmentDuration) < 5) {
      return setErrorMessage({ ...errorMessage, ...{ APPOINTMENT_DURATION: t('APPOINTMENT_DURATION.MESSAGE.APPOINTMENT_DURATION_MORE_THAN_FIVE_MINUTES') } })
    }

    dispatch(submitModal())

    const durationMinute = convertDurationToMinuteOrText(appointmentDuration, appointmentDurationType)
    AppointmentDurationApi.create({ duration: appointmentDuration, durationType: appointmentDurationType, durationMinute })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('APPOINTMENT_DURATION.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()

          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_DURATION.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_DURATION.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const submitEdit = () => {
    if (!appointmentDuration) return setErrorMessage({ ...errorMessage, ...{ DURATION: t(`APPOINTMENT_DURATION.MESSAGE.APPOINTMENT_DURATION_STRING_EMPTY`) } })
    if (appointmentDurationType === 'MINUTES' && Number(appointmentDuration) < 5) {
      return setErrorMessage({ ...errorMessage, ...{ APPOINTMENT_DURATION: t('APPOINTMENT_DURATION.MESSAGE.APPOINTMENT_DURATION_MORE_THAN_FIVE_MINUTES') } })
    }

    dispatch(submitModal())
    const durationMinute = convertDurationToMinuteOrText(appointmentDuration, appointmentDurationType)
    AppointmentDurationApi.update(appointmentDurationId, {
      duration: appointmentDuration,
      durationType: appointmentDurationType,
      durationMinute: durationMinute,
      status: appointmentDurationStatus
    })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`APPOINTMENT_DURATION.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          if (err.message === 'IS_DUPLICATE_APPOINTMENT_DURATION') {

            setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_DURATION.MESSAGE.${err.message}`) } })
          } else setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_DURATION.MESSAGE.${err.message}`) } })

          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`APPOINTMENT_DURATION.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setAppointmentDurationId(0)
    setAppointmentDuration('5')
    setAppointmentDurationType('MINUTES')
    setAppointmentDurationStatus('')
    setIsPopup(false)
    clearErrorMessage()
    setUseWith('')
  }

  const onActive = () => {
    if (id) {
      AppointmentDurationApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('APPOINTMENT_DURATION.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('APPOINTMENT_DURATION.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = () => {
    if (id) {
      AppointmentDurationApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('APPOINTMENT_DURATION.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('APPOINTMENT_DURATION.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { appointmentDurationId: aptDurationId, duration: aptDuration, durationType: aptDurationType, status: aptDurationStatus } = data
    setAppointmentDurationId(aptDurationId)
    setAppointmentDuration(aptDuration)
    setAppointmentDurationType(aptDurationType)
    setAppointmentDurationStatus(aptDurationStatus)
    setIsPopup(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }
  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }
  const handleChangeDurationType = (type: string) => {
    setAppointmentDurationType(type)
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'durationMinute', disablePadding: false, label: t('APPOINTMENT_DURATION.TABLE.CELL.DURATION') },
    { id: 'status', disablePadding: false, label: t('APPOINTMENT_DURATION.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('APPOINTMENT_DURATION.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { appointmentDurationId: aptDurationId, duration: aptDuration, durationType: aptDurationType, updatedBy, updatedAt, status: aptDurationStatus } = objData
    const statusBtnActive = aptDurationStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = aptDurationStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: aptDurationId,
      id: aptDurationId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: t(`APPOINTMENT_DURATION.TABLE.CELL.${aptDurationType}`, { duration: aptDuration }) },
        { option: 'STATUS', align: 'center', label: aptDurationStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }
  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(t(`APPOINTMENT_DURATION.TABLE.CELL.${obj.durationType}`, { duration: obj.duration }))
  }
  return (
    <div className={classes.root}>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`APPOINTMENT_DURATION.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`APPOINTMENT_DURATION.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { appointmentDuration: name, status: t(`APPOINTMENT_DURATION.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <Card className="border-0 h-100">
        <HeaderCard text={t('APPOINTMENT_DURATION.TITLE')} />
        <Card.Body>
          <Card.Title className={'mb-0 pl-xl-3'}>
            <Row className="align-items-center">
              <Col md={8}>
                <Row className="flax-row">
                  <Col sm={12} md={6} xl={4} className="pt-2">
                    <TextInput
                      label={t('APPOINTMENT_DURATION.SEARCH')}
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      fullWidth
                    />
                  </Col>
                  <Col sm={12} md={6} xl={4} className="pl-md-0 pt-2">
                    <SelectInput
                      label={t('STATUS.LABEL')}
                      onChange={(e) => setStatus(e.target.value)}
                      options={[
                        { value: 'ALL', name: t('STATUS.ALL') },
                        { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                        { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
                      ]}
                      value={status}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4} className="mt-2 mt-md-auto">
                <div className="pr-xl-2">
                  <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={() => setIsPopup(true)} textButton={t('APPOINTMENT_DURATION.BUTTON.ADD')} className="w-auto ml-auto d-flex mt-auto" />
                </div>
              </Col>
            </Row>
          </Card.Title>

          <div className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={appointmentDurations.map((val, i) => {
                return renderData(val, i)
              })}
            />
          </div>
        </Card.Body>
      </Card>

      <PageModal
        title={appointmentDurationId ? t('APPOINTMENT_DURATION.TITLE_UPDATE') : t('APPOINTMENT_DURATION.TITLE_CREATE')}
        isOpen={isPopup}
        onCancel={{ onClick: () => resetForm() }}
        onSubmit={{ onClick: () => appointmentDurationId ? submitEdit() : submit() }}
        handleClose={resetForm}
        size={'XS'}
      >
        <Box className="py-2">
          {appointmentDurationId ? (
            <Form className="mx-0 mt-0 mb-3 p-0  d-inline-flex w-100">
              <Form.Label className=" mr-auto">{t('INPUT.LABEL_STATUS')}</Form.Label>
              <SwitchButton
                onChange={() => setAppointmentDurationStatus(appointmentDurationStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                checked={appointmentDurationStatus === 'ACTIVE' ? true : false}
              />
            </Form>
          ) : (
            ''
          )}
          <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
            <Col>
              <TextInput
                fullWidth
                onChange={(event) => {
                  setAppointmentDuration(String(Number(event.target.value) || 0))
                  clearErrorMessage()
                }}
                value={appointmentDuration}
                label={t('APPOINTMENT_DURATION.INPUT.DURATION')}
                helperText={
                  errorMessage.APPOINTMENT_DURATION ||
                  errorMessage.IS_DUPLICATE_APPOINTMENT_DURATION ||
                  errorMessage.APPOINTMENT_DURATION_STRING_BASE ||
                  errorMessage.APPOINTMENT_DURATION_STRING_EMPTY ||
                  errorMessage.APPOINTMENT_DURATION_MAXIMUM_LENGTH ||
                  errorMessage.APPOINTMENT_DURATION_ANY_REQUIRED
                }
                required={true}
              />
            </Col>
            <Col>
              <SelectInput
                onChange={(event) => {
                  const value = event.target.value
                  if (value) {
                    handleChangeDurationType(value)
                    clearErrorMessage()
                  }
                }}
                label={t('APPOINTMENT_DURATION.INPUT.DURATION_TYPE')}
                value={appointmentDurationType}
                options={[{ value: "MINUTES", name: t('TIME_DURATION_TYPE.MINUTES') },
                { value: "HOURS", name: t('TIME_DURATION_TYPE.HOURS') },
                { value: "DAY", name: t('TIME_DURATION_TYPE.DAY') },]}
              />
            </Col>
          </Box>
        </Box>
      </PageModal>

    </div>
  )
}
